import React from 'react'
import moment from 'moment'
import { Button, Spinner } from 'reactstrap'
import SportIcon from './SportIcon'
import { getTimeStops } from './ViewUtils'

const APPINITLANG = 'fi'

// How many places a court offers at its start time: one for a real court, and for the merged
// `multipleconfig` court every member court free for at least one of its durations.
const placesOf = (court) => {
  const ids = new Set()
  ;(court.times || []).forEach((time) => (time.resourceIds || []).forEach((id) => ids.add(id)))
  return ids.size > 0 ? ids.size : 1
}

// Read-only availability grid for embedding: one row per time stop showing per-category
// availability counts (e.g. "Tennis: 3", "Sulkapallo: 5"), never the court identities.
// Venues without categories get a single total count per row.
function ReadOnlyView({ dailyFreeTimes, appconfig, onSlotClick, loading, t }) {
  const categories = appconfig.categories || []

  // countsByTime: 'HH:mm' -> { [category]: count } ('' key = total for no-category venues)
  const countsByTime = {}
  for (const entry of dailyFreeTimes || []) {
    if (!entry || !entry.start || !entry.courts) continue
    // courts with empty times are the synthetic no-vacancy bucket from formatVacancies
    const courts = entry.courts.filter((c) => c.times && c.times.length > 0)
    if (courts.length === 0) continue
    const key = moment.utc(entry.start).format('HH:mm')
    if (!countsByTime[key]) countsByTime[key] = {}
    const total = (list) => list.reduce((sum, c) => sum + placesOf(c), 0)
    if (categories.length > 0) {
      categories.forEach((cat) => {
        const count = total(courts.filter((c) => c.category === cat.category))
        if (count > 0) {
          countsByTime[key][cat.category] = (countsByTime[key][cat.category] || 0) + count
        }
      })
    } else {
      countsByTime[key][''] = (countsByTime[key][''] || 0) + total(courts)
    }
  }

  let s = '06:00'
  let e = '23:00'
  let i = 30
  if (appconfig.times) {
    s = appconfig.times.s || s
    e = appconfig.times.e || e
    i = appconfig.times.i || i
  }
  const stops = getTimeStops(s, e, i)

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 30 }}>
        <Spinner size='sm' color='secondary' />
      </div>
    )
  }

  const hasAny = Object.keys(countsByTime).length > 0

  if (!hasAny) {
    return (
      <div style={{ textAlign: 'center', padding: 30, color: '#888' }}>
        {t('no_vacancy')}
      </div>
    )
  }

  const categoryLabel = (cat) =>
    cat.label && cat.label[APPINITLANG] ? cat.label[APPINITLANG] : cat.category

  if (categories.length === 0) {
    return (
      <div>
        {stops.map((stop) => {
          const counts = countsByTime[stop]
          return (
            <div
              key={stop}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: '1px solid #eee',
                padding: '4px 8px',
              }}
            >
              <span style={{ fontWeight: 'bold', marginRight: 10 }}>{stop}</span>
              {counts ? (
                <Button size='sm' color='success' outline onClick={() => onSlotClick(null)}>
                  {t('free_count', { count: counts[''] })}
                </Button>
              ) : (
                <span style={{ color: '#bbb' }}>—</span>
              )}
            </div>
          )
        })}
      </div>
    )
  }

  // grid: fixed-width time column + one equal column per category; the fixed first
  // column keeps the header row and the count rows aligned (each row is its own grid)
  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: '56px repeat(' + categories.length + ', 1fr)',
    alignItems: 'center',
    columnGap: 4,
    minWidth: 56 + categories.length * 72,
  }

  return (
    <div>
      <div
        style={{
          ...gridStyle,
          borderBottom: '2px solid #ddd',
          padding: '4px 8px',
          // keep category names visible while scrolling the day; sits below the
          // sticky 40px date selector of ReadOnlyScreen
          position: 'sticky',
          top: 40,
          background: '#fff',
          zIndex: 2,
        }}
      >
        <span />
        {categories.map((cat) => (
          <span
            key={'head-' + cat.category}
            style={{ textAlign: 'center', fontWeight: 'bold', fontSize: '0.8em', whiteSpace: 'nowrap' }}
          >
            {categoryLabel(cat)}
          </span>
        ))}
      </div>
      {stops.map((stop) => {
        const counts = countsByTime[stop]
        return (
          <div key={stop} style={{ ...gridStyle, borderBottom: '1px solid #eee', padding: '4px 8px' }}>
            <span style={{ fontWeight: 'bold', marginRight: 6 }}>{stop}</span>
            {categories.map((cat) => (
              <span key={stop + '-' + cat.category} style={{ textAlign: 'center' }}>
                {counts && counts[cat.category] ? (
                  <Button
                    size='sm'
                    color='success'
                    outline
                    style={{ stroke: '#28a745', whiteSpace: 'nowrap' }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.stroke = '#fff'
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.stroke = '#28a745'
                    }}
                    onClick={() => onSlotClick(cat.category)}
                  >
                    <SportIcon
                      style={{ marginRight: 3, marginBottom: 2 }}
                      sport={cat.icon ? cat.icon.toLowerCase() : categoryLabel(cat).toLowerCase()}
                      fontSize={'small'}
                    />
                    {counts[cat.category]}
                  </Button>
                ) : (
                  <span style={{ color: '#ccc' }}>—</span>
                )}
              </span>
            ))}
          </div>
        )
      })}
    </div>
  )
}

export default ReadOnlyView
